/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React from 'react'
import { useIntl } from 'react-intl'

import {
  EuiCheckableCard,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormFieldset,
  EuiSpacer,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { ObservabilityProjectProductTier } from '@modules/project-user-api/v1/types'

import observabilityTiersOptions from './observabilityTiersOptions'

export interface TierOptionProps {
  slug: ObservabilityProjectProductTier
  title: string
  description: string
  properties: string[]
  checked: boolean
  onChange: (value: ObservabilityProjectProductTier) => void
  disabled?: boolean
}

type Props = {
  value: ObservabilityProjectProductTier
  onChange: (value: ObservabilityProjectProductTier) => void
  hideTitle?: boolean
  disabledTiers?: ObservabilityProjectProductTier[]
}

const SelectObservabilityTier: React.FunctionComponent<Props> = ({
  value,
  onChange,
  hideTitle,
  disabledTiers = [],
}) => {
  const sectionTitle = hideTitle ? null : (
    <EuiTitle size='xs'>
      <FormattedMessage
        id='createProject.observability.subType.title'
        defaultMessage='Product features'
      />
    </EuiTitle>
  )

  const { formatMessage } = useIntl()

  return (
    <EuiFormFieldset
      legend={{
        children: sectionTitle,
      }}
    >
      <EuiFlexGroup
        gutterSize='m'
        responsive={false}
        alignItems='flexStart'
        justifyContent='spaceEvenly'
      >
        {observabilityTiersOptions.map(({ slug, title, description, properties }, i) => (
          <EuiFlexItem key={slug}>
            <TierOption
              slug={slug}
              title={formatMessage(title)}
              description={formatMessage(description)}
              properties={properties.map((p) => formatMessage(p))}
              checked={slug === value}
              key={i}
              onChange={onChange}
              disabled={disabledTiers.includes(slug)}
            />
          </EuiFlexItem>
        ))}
      </EuiFlexGroup>
    </EuiFormFieldset>
  )
}

const TierOption = ({
  slug,
  title,
  description,
  properties,
  checked,
  onChange,
  disabled,
}: TierOptionProps) => {
  const htmlId = useGeneratedHtmlId()

  const label = (
    <EuiFlexItem>
      <div style={{ whiteSpace: 'nowrap' }}>
        <strong>{title}</strong>
      </div>
      <EuiSpacer size='s' />
      <EuiText size='s'>{description}</EuiText>
      <EuiSpacer size='s' />
      <EuiText size='s'>
        <ul>
          {properties.map((property, index) => (
            <li key={index}>{property}</li>
          ))}
        </ul>
      </EuiText>
    </EuiFlexItem>
  )

  return (
    <EuiFlexGroup justifyContent='center'>
      <EuiFlexItem>
        <EuiCheckableCard
          id={`${htmlId}${slug}`}
          data-test-id={`radio-input-${slug}`}
          label={label}
          checked={checked}
          onChange={() => onChange(slug)}
          disabled={disabled}
        />
      </EuiFlexItem>
    </EuiFlexGroup>
  )
}

export default SelectObservabilityTier
